import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
  viewChildren,
  WritableSignal,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-enterprise";

import { ConfirmDialogComponent, TableComponent } from "@components/shared";
import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { QuickActionsMenuComponent } from "@shared/cell-renderers";
import { IconConstants, TextConstants } from "@shared/constants";
import { AuthenticationService, NotificationService } from "@shared/services";
import { ColumnUtils, CommonUtils, LocalizationUtils } from "@shared/utils";

import { RiskAssessmentReportsApiService } from "../../../api/";
import {
  relatedRiskAssessmentReportResourceTypesMap,
  riskAssessmentReportOrderedResourceTypes,
  riskAssessmentReportResourceMap,
} from "../../../constants";
import {
  RelatedRiskAssessmentReportsTableModel as Model,
  RiskAssessmentReportRecord,
} from "../../../models";
import {
  RiskAssessmentReportRelatedRecordsService,
  RiskAssessmentReportsRecordService,
  RiskAssessmentReportsRiskLevelTagsService,
} from "../../../services";

export type ReferenceReportEvent =
  | { type: "single"; uri: string; action: "add" | "remove" }
  | { type: "multiple"; uris: string[] };

interface Resource {
  type: RiskAssessmentTemplateResourceType;
  label: string;
  isRelated: boolean;
}

@Component({
  selector: "app-risk-assessment-report-overlay-references",
  templateUrl: "./risk-assessment-report-overlay-references.component.html",
  styleUrls: ["./risk-assessment-report-overlay-references.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RiskAssessmentReportOverlayReferencesComponent implements OnChanges {
  readonly overlay = inject(SlideOverlayPageService);

  readonly tagsService = inject(RiskAssessmentReportsRiskLevelTagsService);

  private readonly dialog = inject(MatDialog);

  private readonly authService = inject(AuthenticationService);

  private readonly notificationService = inject(NotificationService);

  private readonly reportsApi = inject(RiskAssessmentReportsApiService);

  private readonly reportsRecordService = inject(RiskAssessmentReportsRecordService);

  private readonly relatedRecordsService = inject(RiskAssessmentReportRelatedRecordsService);

  readonly reportsPanel = viewChild("reportsPanel", { read: TemplateRef });

  private readonly countryFlag = viewChild("countryFlag", {
    read: TemplateRef<{ $implicit: string }>,
  });

  private readonly tables = viewChildren(TableComponent);

  readonly reportUris = input.required<string[]>();

  readonly type = input.required<RiskAssessmentTemplateResourceType>();

  readonly entityId = input.required<string>();

  readonly canEdit = input(false);

  readonly referenceReport = output<ReferenceReportEvent>();

  readonly reportIds = signal<string[]>([]);

  readonly selectedResourceType = signal<RiskAssessmentTemplateResourceType>(undefined);

  readonly resources = signal<Resource[]>([]);

  readonly columnDefs = signal<ColDef[]>([]);

  readonly relatedColumnDefs = signal<ColDef[]>([]);

  readonly showNoRecordsMessage = computed(
    () =>
      this.resources().every(({ isRelated }) => !isRelated) &&
      Array.from(this.rowDataMap.values()).every((records) => !records().length) &&
      Array.from(this.isLoadingMap.values()).every((isLoading) => !isLoading()),
  );

  readonly allRelatedReportUris = computed(() => {
    return this.resources()
      .filter(({ isRelated }) => isRelated)
      .map(({ type }) => this.relatedRowDataMap.get(type)())
      .flat()
      .map(({ id }) => this.getUri(id));
  });

  readonly rowDataMap = new Map<
    RiskAssessmentTemplateResourceType,
    WritableSignal<RiskAssessmentReportRecord[]>
  >();

  readonly relatedRowDataMap = new Map<
    RiskAssessmentTemplateResourceType,
    WritableSignal<RiskAssessmentReportRecord[]>
  >();

  private readonly intitalRelatedRowDataMap = new Map<
    RiskAssessmentTemplateResourceType,
    WritableSignal<RiskAssessmentReportRecord[]>
  >();

  readonly isLoadingMap = new Map<RiskAssessmentTemplateResourceType, WritableSignal<boolean>>();

  readonly relatedResources = relatedRiskAssessmentReportResourceTypesMap;

  readonly translations = {
    norecords: $localize`:@@riskassessmentreportoverlay.references.norecords:No referenced risk assessments`,
    clickaddbutton: $localize`:@@riskassessmentreportoverlay.references.clickaddbutton:To reference a risk assessment, click the "+ Add" button`,
    suggested: $localize`:@@riskassessmentreportoverlay.references.suggested:Suggested`,
    addall: TextConstants.ADD_ALL,
    add: TextConstants.ADD,
  };

  private readonly resourceTypeMap = Object.fromEntries(
    Object.entries(riskAssessmentReportResourceMap).map(([key, value]) => [value, key]),
  ) as Record<string, RiskAssessmentTemplateResourceType>;

  constructor() {
    this.tagsService.init();
  }

  async ngOnChanges(changes: SimpleChanges) {
    const reportUris = changes["reportUris"];

    if (reportUris) {
      if (reportUris.firstChange) {
        this.setResources();
        this.setColumnDefs();
        await this.getAllRelated();
      }

      this.reportIds.set(this.reportUris().map((uri) => CommonUtils.getUriId(uri)));
      await this.getAll();
    }
  }

  openReportsPanel(type: RiskAssessmentTemplateResourceType) {
    this.selectedResourceType.set(type);
    this.overlay.openSideMenu(this.reportsPanel());
  }

  onAdd({ id, record }: RiskAssessmentReportRecord) {
    this.isLoadingMap.get(record.type).set(true);
    this.referenceReport.emit({ type: "single", uri: this.getUri(id), action: "add" });
  }

  onAddAll(type: RiskAssessmentTemplateResourceType) {
    const uris = this.relatedRowDataMap
      .get(type)()
      .map(({ id }) => this.getUri(id));

    this.referenceReport.emit({ type: "multiple", uris });
  }

  onSelectedTag(value: string) {
    const model = this.createFilterModel(value);

    this.tagsService.update(value);

    // TODO: find solution without setTimeout
    setTimeout(() => {
      this.tables().forEach((table) => {
        const api = table.grid()?.api;

        api?.setColumnFilterModel<Model.FilterModel>("residualRisk", model);
        api?.onFilterChanged();
      });
    }, 1);
  }

  private getUri(id: string) {
    return `/organisations/${this.authService.getActiveOrganisationId()}/risk-assessment/reports/${id}`;
  }

  private setResources() {
    this.resources.set(
      riskAssessmentReportOrderedResourceTypes.map((type) => ({
        type,
        label: LocalizationUtils.getRelatedRiskAssessmentRecordType[type],
        isRelated: relatedRiskAssessmentReportResourceTypesMap[this.type()].includes(type),
      })),
    );

    this.resources().forEach(({ type }) => {
      this.rowDataMap.set(type, signal([]));
      this.intitalRelatedRowDataMap.set(type, signal([]));
      this.relatedRowDataMap.set(type, signal([]));
      this.isLoadingMap.set(type, signal(true));
    });
  }

  private setColumnDefs() {
    const columnDefs = (action: "add" | "delete") => [
      {
        headerName: TextConstants.NAME_ID,
        field: "record.name",
        ...ColumnUtils.quickActionsMenuColumnCommonValues,
        cellRenderer: QuickActionsMenuComponent,
        cellRendererParams: (params: ICellRendererParams<RiskAssessmentReportRecord>) => {
          let templateWithContext: { template: TemplateRef<any>; context: any } = null;

          if (params.data.record.type === RiskAssessmentTemplateResourceType.COUNTRY) {
            templateWithContext = {
              template: this.countryFlag(),
              context: {
                $implicit: params?.data.record.code,
              },
            };
          }

          let actions = [];

          if (this.canEdit() && action) {
            if (action === "add") {
              actions = [
                {
                  click: (row) => this.onAdd(row),
                  tooltip: TextConstants.ADD,
                  icon: "add",
                },
              ];
            } else if (action === "delete") {
              actions = [
                {
                  click: (row) => this.onDelete(row),
                  tooltip: TextConstants.REMOVE,
                  icon: "close",
                },
              ];
            }
          }

          return {
            templateWithContext,
            actions,
          };
        },
      },
      ColumnUtils.dateColumn({
        headerName: TextConstants.DATE_COMPLETED,
        field: "completedDate",
        sort: "desc",
      }),
      {
        ...ColumnUtils.tags(TextConstants.RESIDUAL_RISK, "residualRisk"),
        filter: "agTextColumnFilter",
        filterParams: {
          readOnly: true,
        },
        filterValueGetter: (params: ValueGetterParams<RiskAssessmentReportRecord>) => {
          if (!params.data?.residualRisk) {
            return null;
          }

          return this.tagsService.getId(params.data.residualRisk);
        },
      },
      ColumnUtils.dateColumn({
        headerName: TextConstants.VALID_UNTIL,
        field: "validUntilDate",
      }),
    ];

    this.columnDefs.set(columnDefs("delete"));
    this.relatedColumnDefs.set(columnDefs("add"));
  }

  private async getAll() {
    const existingIds: string[] = [];

    for (const [_, reports] of this.rowDataMap.entries()) {
      reports.update((currentReports) =>
        currentReports.filter((report) => {
          const reportExists = this.reportIds().includes(report.id);

          if (reportExists) {
            existingIds.push(report.id);
          } else {
            this.tagsService.remove(report);

            if (!this.tagsService.exists(report.residualRisk)) {
              this.onSelectedTag(Model.ALL_RISK_LEVELS.value);
            }
          }

          return reportExists;
        }),
      );
    }

    const newIds = this.reportIds().filter((id) => existingIds.indexOf(id) === -1);

    try {
      const newReports = await Promise.all(newIds.map((id) => this.reportsApi.get(id)));

      newReports.forEach((report) => {
        const type = report.recordUris[0].split("/").slice(-2, -1)[0];
        const resource = this.resourceTypeMap[type];

        if (!resource) {
          throw new Error(TextConstants.UNSUPPORTED_RESOURCE_TYPE);
        }

        this.isLoadingMap.get(resource).set(true);
        this.rowDataMap
          .get(resource)
          .update((currentReports) => [...currentReports, { ...report, record: undefined }]);
        this.tagsService.add(report);
      });

      await Promise.all(
        Array.from(this.rowDataMap.entries()).map(async ([resource, reports]) => {
          const reportsWithoutRecord = reports().filter((report) => !report.record);
          const reportsWithRecord = await this.reportsRecordService.get(
            reportsWithoutRecord,
            resource,
          );

          reportsWithRecord.forEach(({ id, record }) => {
            reports.update((currentReports) =>
              currentReports.map((report) => (report.id === id ? { ...report, record } : report)),
            );
          });
        }),
      );

      this.resources().forEach(({ type, isRelated }) => {
        const initialRelatedReports = this.intitalRelatedRowDataMap.get(type)();

        if (isRelated && initialRelatedReports.length) {
          const referecedReports = this.rowDataMap.get(type)();
          const relatedReports = this.relatedRowDataMap.get(type);
          const referencedIds = new Set(referecedReports.map(({ id }) => id));

          const filteredRelatedReports = relatedReports().filter(
            ({ id }) => !referencedIds.has(id),
          );

          const filteredInitialRelatedReports = initialRelatedReports.filter(
            ({ id }) =>
              !referencedIds.has(id) && !filteredRelatedReports.some((report) => report.id === id),
          );

          relatedReports.set([...filteredRelatedReports, ...filteredInitialRelatedReports]);
        }
      });

      for (const key of this.isLoadingMap.keys()) {
        this.isLoadingMap.get(key).set(false);
      }
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  private async getAllRelated() {
    const relatedRecords = await this.relatedRecordsService.get({
      id: this.entityId(),
      type: this.type(),
    });

    for (const resource of this.resources()) {
      if (resource.isRelated) {
        const records = relatedRecords.filter(({ record }) => record.type === resource.type);

        this.intitalRelatedRowDataMap.get(resource.type).set(records);
      }
    }
  }

  private onDelete({ id, record }: RiskAssessmentReportRecord) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.REMOVE_CONFIRMATION,
        contentText: $localize`:@@confirm.referencedriskassessment.remove:Are you sure you want to remove this risk assessment from the referenced risk assessments?`,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.REMOVE,
        confirmButtonIcon: IconConstants.DELETE,
        onSubmit: () => {
          this.isLoadingMap.get(record.type).set(true);
          this.referenceReport.emit({ type: "single", uri: this.getUri(id), action: "remove" });
        },
      },
    });
  }

  private createFilterModel(value: string): Model.FilterModel {
    if (value === Model.NONE_RISK_LEVEL.value) {
      return {
        filterType: "text",
        type: "blank",
        filter: null,
      };
    }

    if (value !== Model.ALL_RISK_LEVELS.value) {
      return {
        filterType: "text",
        type: "equals",
        filter: value,
      };
    }

    return null;
  }
}
